import { useState, useMemo, useEffect } from "react";

import {
  CheckCircle2,
  XCircle,
  RotateCcw,
  Clock,
  User,
  Calendar,
  MessageSquare,
  AlertTriangle,
  Eye,
  CloudLightning
} from "lucide-react";
import { Card, CardContent } from "../../../ui/card";
import { Badge } from "../../../ui/badge";
import { getStatusColor } from "../utils";
import { Button } from "../../../ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../../ui/dialog";
import { AwsDataAccess, ResourceRequestsProps } from "../resourceRequestInterface";
import { changeAWSStatus, getAWSNotificationAPI, getAWSNotificationCountAPI, revokeRequest } from "../Api/resourcerequestapi";
import { myLocalStorage } from "../../../StorageHelper";
import useUserStore from "../../../../services/userStore";
import { format } from "date-fns";
import { formatDate } from "../../formatTimeAgo";
import { RESOURCE_REQUEST_POLL_MS } from "../resourceRequestPolling";
import { Skeleton } from "../../interfaces/CommonInterfaces";
import MultiApprovalProgressPanel from "../../MultiApprovals/MultiApprovalProgressPanel";
import MultiApprovalGroupedHistory from "../../MultiApprovals/MultiApprovalGroupedHistory";
import MultiApprovalListSummary from "../../MultiApprovals/MultiApprovalListSummary";
import { isMultiApprovalRequest } from "../../MultiApprovals/Api/multiapprovalapi";
import { useMultiApprovalWorkflow } from "../hooks/useMultiApprovalWorkflow";

export default function AWSResourceRequests({
  computerName = "",
  searchTerm = "",
  statusFilter = "",
  startDate,
  endDate,
  activeTab
}: ResourceRequestsProps) {
  const [selectedRequest, setSelectedRequest] = useState<AwsDataAccess | null>(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [notifications, setNotifications] = useState<AwsDataAccess[]>([]);
  const [page, setPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  const tenantName = useMemo(
    () => myLocalStorage.getItem("latestTenant").tenantName || "",
    []
  );

  const { email, role } = useUserStore((state) => state.user);
  const [loadingIds, setLoadingIds] = useState<Set<number>>(new Set());

  const {
    detailsWorkflow,
    openDetailsWorkflow,
    closeDetailsWorkflow,
    canUserApprove,
    canUserDecline,
    canUserDeclineDetails,
    getWorkflowForRequest,
  } = useMultiApprovalWorkflow(notifications);

  const handleViewDetails = (request: AwsDataAccess) => {
    setSelectedRequest(request);
    setIsDetailsOpen(true);
    openDetailsWorkflow(request);
  };



  const getStatusIcon = (status: string) => {
    switch (status) {
      case "PENDING":
        return <Clock className="w-4 h-4" />;
      case "APPROVED":
        return <CheckCircle2 className="w-4 h-4" />;
      case "DECLINED":
        return <XCircle className="w-4 h-4" />;
      case "REVOKED":
        return <RotateCcw className="w-4 h-4" />;
      case "EXPIRED":
        return <AlertTriangle className="w-4 h-4" />;
      default:
        return <Clock className="w-4 h-4" />;
    }
  };

  const handleRequest = async (
    status: string,
    id: number,
    notification: AwsDataAccess
  ) => {
    setLoadingIds((current) => new Set(current).add(id));
    try {
      let payload = {
        accessId: id,
        requestStatus: status,
        accessType: notification.accessType,
        arn: notification.accessARN,
        name: notification.accessName,
        userName: notification.awsUsername,
        policy: notification.selectedPolicies,
      }
      await changeAWSStatus(tenantName, payload);
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingIds((current) => {
        const next = new Set(current);
        next.delete(id);
        return next;
      });
    }

    await getAWSNotification(false);
  };

  const handleRevoke = async (accessId: number) => {
    setLoadingIds((current) => new Set(current).add(accessId));
    try {
      const revoked = await revokeRequest("aws", accessId);
      if (revoked !== true) {
        throw new Error("Failed to cancel or revoke AWS request");
      }
      setIsDetailsOpen(false);
      await getAWSNotification(false);
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingIds((current) => {
        const next = new Set(current);
        next.delete(accessId);
        return next;
      });
    }
  };

  const fetchAWSCount = async () => {
    const response = await getAWSNotificationCountAPI(
      computerName,
      searchTerm,
      statusFilter.toUpperCase(),
      startDate ? format(startDate, "yyyy-MM-dd") : "",
      endDate ? format(endDate, "yyyy-MM-dd") : "",
      tenantName,
      page,
      email,
      role
    )
    setTotalItems(response || 0);
  }

  const getAWSNotification = async (isFirst = true) => {
    setIsLoading(isFirst);
    try {
      const response: AwsDataAccess[] = await getAWSNotificationAPI(
        computerName,
        searchTerm,
        statusFilter.toUpperCase(),
        startDate ? format(startDate, "yyyy-MM-dd") : "",
        endDate ? format(endDate, "yyyy-MM-dd") : "",
        tenantName,
        page - 1,
        email,
        role
      );
      setNotifications(response);
    } catch (error) {
      console.error("Error fetching notifications:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAWSCount();
    getAWSNotification();
    if (activeTab === "aws") {
      const interval = setInterval(() => {
        fetchAWSCount();
        getAWSNotification(false);
      }, RESOURCE_REQUEST_POLL_MS)
      return () => clearInterval(interval)
    }
  }, [
    computerName,
    searchTerm,
    statusFilter,
    startDate,
    endDate,
    tenantName,
    page,
    email,
    role,
    activeTab
  ]);
  const totalPages = Math.ceil(totalItems / 15);

  return (
    <div className="space-y-1">
      {isLoading ? (
        <>
          {[...Array(3)].map((_, idx) => (
            <Card key={idx} className="border-slate-200 dark:border-slate-700 hover-elevate cursor-pointer transition-transform hover:scale-[1.01]">
              <CardContent className="p-3 space-y-3">
                <div className="flex justify-between items-center">
                  <Skeleton className="w-40 h-4" />
                  <Skeleton className="w-12 h-4 ml-auto" />
                </div>
                <Skeleton className="w-64 h-3" />
                <Skeleton className="w-32 h-3" />
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Skeleton className="w-full h-3" />
                  <Skeleton className="w-full h-3" />
                  <Skeleton className="w-full h-3" />
                  <Skeleton className="w-full h-3" />
                </div>
              </CardContent>
            </Card>
          ))}
        </>
      ) : notifications.length > 0 ? (
        <>

          {/* Request Cards */}
          {notifications.map((request) => (
            <Card
              key={request.id}
              className="border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 hover:border-slate-300 dark:hover:border-slate-600 transition-all duration-200"
              data-testid={`aws-request-card-${request.id}`}
            >
              <CardContent className="p-2">
                <div className="flex items-start gap-3">

                  <div className="flex-1 space-y-1">
                    {/* Header */}
                    <div className="flex items-start justify-between">
                      <div className="space-y-1">
                        <div className="flex items-center gap-1.5">
                          <CloudLightning className="w-3.5 h-3.5 text-orange-500" />
                          <h3 className="font-medium text-sm text-slate-800 dark:text-slate-100">
                            {request.accessName} Access
                          </h3>
                          <Badge className={`border text-xs px-1.5 py-0.5 ${getStatusColor(request.requestStatus)}`}>
                            <div className="flex items-center gap-1">
                              {getStatusIcon(request.requestStatus)}
                              {request.requestStatus}
                            </div>
                          </Badge>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-slate-600 dark:text-slate-300">
                          <div className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {formatDate(request.createdAt)}
                          </div>
                          <div className="flex items-center gap-1">
                            <User className="w-3 h-3" />
                            {request.email}
                          </div>
                        </div>
                      </div>

                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleViewDetails(request)}
                        className="text-xs px-2 py-1 h-auto"
                        data-testid={`button-details-aws-${request.id}`}
                      >
                        <Eye className="w-3 h-3 mr-1" />
                        Details
                      </Button>
                    </div>

                    {/* Content */}
                    <div className="grid grid-cols-4 gap-3 text-sm">
                      <div>
                        <span className="text-slate-500 dark:text-slate-400">Access Name:</span>
                        <div className="font-medium text-slate-700 dark:text-slate-200">{request.accessName}</div>
                      </div>
                      <div>
                        <span className="text-slate-500 dark:text-slate-400">Policies:</span>
                        <div className="font-medium text-slate-700 dark:text-slate-200">{request.selectedPolicies}</div>
                      </div>
                      <div>
                        <span className="text-slate-500 dark:text-slate-400">Access Type:</span>
                        <div className="font-medium text-slate-700 dark:text-slate-200">{request.accessType}</div>
                      </div>
                      <div>
                        <span className="text-slate-500 dark:text-slate-400">Duration:</span>
                        <div className="font-medium text-slate-700 dark:text-slate-200">
                          {request.privilegeLevel === "PERMANENT" ? "Permanent" : `${request.selectedTime} min`}

                        </div>
                      </div>
                    </div>
                    {request.schedules && request.schedules.length > 0 && (
                      <div className="p-3 bg-blue-50 dark:bg-blue-950/20 rounded-lg border border-blue-200 dark:border-blue-800">
                        <div className="flex items-center gap-2 mb-2">
                          <Calendar className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                          <span className="text-sm font-medium text-blue-700 dark:text-blue-300">
                            Scheduled Access
                          </span>
                        </div>

                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <label className="text-blue-600 dark:text-blue-400 font-medium">
                              Start Time
                            </label>
                            <p className="text-blue-800 dark:text-blue-200">
                              {request.schedules[0]?.scheduledStartTime
                                ? formatDate(request.schedules[0].scheduledStartTime)
                                : "—"}
                            </p>
                          </div>

                          <div>
                            <label className="text-blue-600 dark:text-blue-400 font-medium">
                              End Time
                            </label>
                            <p className="text-blue-800 dark:text-blue-200">
                              {request.schedules[0]?.scheduledEndTime
                                ? formatDate(request.schedules[0].scheduledEndTime)
                                : "—"}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {((request.approvalEmailId && request.approvalEmailId.length > 0) || request.revokedBy) && (
                      <div className="p-3 bg-amber-50 dark:bg-amber-950/20 rounded-lg border border-amber-200 dark:border-amber-800">
                        <div className="text-xs text-amber-600 dark:text-amber-400 font-medium mb-2">
                          Admin Actions
                        </div>

                        <div className="space-y-1 text-sm">
                          {request.approvalEmailId && request.approvalEmailId.length > 0 && (
                            <p className="text-green-700 dark:text-green-300">
                              ✓ Approved by {request.approvalEmailId[0]} on{" "}
                              {formatDate(request.createdAt)}
                            </p>
                          )}

                          {request.revokedBy && (
                            <p className="text-purple-700 dark:text-purple-300">
                              ⊝ Revoked by {request.revokedBy} on{" "}
                              {formatDate(request.createdAt ?? new Date().toISOString())}
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                    <div className="grid grid-cols-2 gap-4 p-3 text-sm border border-green-200 rounded-lg md:grid-cols-3 bg-green-50 dark:bg-green-950/20 dark:border-green-800">
                      <div>
                        <label className="font-medium text-green-600 dark:text-green-400">
                          Account Id
                        </label>
                        <p className="font-mono text-xs text-green-800 dark:text-green-200">
                          {request.awsAccountId}
                        </p>
                      </div>
                      <div>
                        <label className="font-medium text-green-600 dark:text-green-400">
                          User Name
                        </label>
                        <p className="text-xs text-green-800 dark:text-green-200">
                          {request.awsUsername}
                        </p>
                      </div>
                    </div>

                    {(() => {
                      const workflow = getWorkflowForRequest(request.id);
                      return isMultiApprovalRequest(workflow) ? (
                        <MultiApprovalListSummary workflow={workflow} />
                      ) : null;
                    })()}

                    {/* Actions */}
                    <div className="flex items-center justify-between pt-2 border-t border-slate-100 dark:border-slate-700">
                      <div className="flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400">
                        <MessageSquare className="w-3 h-3" />
                        {request.requestMessage}
                      </div>
                      <div className="flex items-center gap-2">
                        {request.requestStatus === "PENDING" && (
                          <>
                            {role !== "TENANT_USER" ? (
                              <>
                                <Button
                                  disabled={loadingIds.has(request.id) || !canUserApprove(request.id)}
                                  size="sm"
                                  className="text-white bg-green-600 hover:bg-green-700 disabled:opacity-50"
                                  onClick={() => handleRequest("APPROVED", request.id, request)}
                                >
                                  {loadingIds.has(request.id) ? (
                                    <>
                                      <svg
                                        className="animate-spin w-4 h-4 mr-1 text-white"
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                      >
                                        <circle
                                          className="opacity-25"
                                          cx="12"
                                          cy="12"
                                          r="10"
                                          stroke="currentColor"
                                          strokeWidth="4"
                                        ></circle>
                                        <path
                                          className="opacity-75"
                                          fill="currentColor"
                                          d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 00-8 8h4z"
                                        ></path>
                                      </svg>
                                      Approving...
                                    </>
                                  ) : (
                                    <>
                                      <CheckCircle2 className="w-4 h-4 mr-1" /> Approve
                                    </>
                                  )}
                                </Button>
                              <Button
                                size="sm"
                                variant="destructive"
                                  disabled={loadingIds.has(request.id) || !canUserDecline(request.id)}
                                  className="disabled:opacity-50"
                                  onClick={() => handleRequest("DECLINE", request.id, request)}
                              >
                                <XCircle className="w-4 h-4 mr-1" /> Decline
                              </Button>
                            </>
                          ) : (
                            <>
                                <Button
                                  size="sm"
                                  className="text-white bg-green-600 hover:bg-green-700"
                                  onClick={() => setIsDetailsOpen?.(false)}
                                >
                                <CheckCircle2 className="w-4 h-4 mr-1" /> Pending
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                disabled={loadingIds.has(request.id)}
                                className="text-purple-600 border-purple-600 hover:bg-purple-50 dark:hover:bg-purple-950"
                                onClick={() => handleRevoke(request.id)}
                              >
                                <RotateCcw className="w-4 h-4 mr-1" /> Cancel Request
                              </Button>
                            </>
                          )}
                          </>
                        )}

                        {request.requestStatus === "APPROVED" && (
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={loadingIds.has(request.id)}
                            className="text-purple-600 border-purple-600 hover:bg-purple-50 dark:hover:bg-purple-950"
                            onClick={() => handleRevoke(request.id)}
                            data-testid={`button-revoke-aws-${request.id}`}
                          >
                            <RotateCcw className="w-4 h-4 mr-1" />
                            Revoke
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </>
      ) : (
        <Card className="border-slate-200 dark:border-slate-700">
          <CardContent className="p-12 text-center">
            <CloudLightning className="w-12 h-12 mx-auto mb-4 text-orange-300 dark:text-orange-600" />
            <h3 className="text-lg font-medium text-slate-600 dark:text-slate-300 mb-2">No AWS requests found</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              No access requests found for AWS resources
            </p>
          </CardContent>
        </Card>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-between py-4">
          <div className="text-sm text-slate-600 dark:text-slate-300">
            Showing {(page - 1) * 15 + 1}–{Math.min(page * 15, totalItems)} of {totalItems}
          </div>

          <div className="flex items-center space-x-2">
            {/* Previous Button */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(page - 1)}
              disabled={page === 1}
              data-testid="button-prev-page-onprem"
            >
              Previous
            </Button>

            {/* Dynamic Page Numbers */}
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const startPage = Math.max(1, Math.min(page - 2, totalPages - 4));
              const pageNum = startPage + i;

              return (
                <Button
                  key={pageNum}
                  variant={pageNum === page ? "default" : "outline"}
                  size="sm"
                  onClick={() => setPage(pageNum)}
                  className={pageNum === page ? "bg-blue-600 hover:bg-blue-700 text-white" : ""}
                  data-testid={`page-onprem-${pageNum}`}
                >
                  {pageNum}
                </Button>
              );
            })}

            {/* Next Button */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(page + 1)}
              disabled={page === totalPages}
              data-testid="button-next-page-onprem"
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Details Modal */}
      <Dialog
        open={isDetailsOpen}
        onOpenChange={(open) => {
          setIsDetailsOpen(open);
          if (!open) {
            closeDetailsWorkflow();
          }
        }}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>AWS Resource Request Details</DialogTitle>
            <DialogDescription>
              Complete details for the AWS resource access request
            </DialogDescription>
          </DialogHeader>
          {selectedRequest && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium text-slate-600 dark:text-slate-300">Request ID:</span>
                  <p className="text-slate-800 dark:text-slate-100">{selectedRequest.id}</p>
                </div>
                <div>
                  <span className="font-medium text-slate-600 dark:text-slate-300">Status:</span>
                  <p className="text-slate-800 dark:text-slate-100">{selectedRequest.requestStatus}</p>
                </div>
                <div>
                  <span className="font-medium text-slate-600 dark:text-slate-300">User Name:</span>
                  <p className="text-slate-800 dark:text-slate-100">{selectedRequest.awsUsername}</p>
                </div>
                <div>
                  <span className="font-medium text-slate-600 dark:text-slate-300">Requested By:</span>
                  <p className="text-slate-800 dark:text-slate-100">{selectedRequest.email}</p>
                </div>
                <div>
                  <span className="font-medium text-slate-600 dark:text-slate-300">Created:</span>
                  <p className="text-slate-800 dark:text-slate-100">{formatDate(selectedRequest.createdAt)}</p>
                </div>
                <div>
                  <span className="font-medium text-slate-600 dark:text-slate-300">AWS Account:</span>
                  <p className="text-slate-800 dark:text-slate-100">{selectedRequest.awsAccountId}</p>
                </div>
                <div>
                  <span className="font-medium text-slate-600 dark:text-slate-300">Access Name:</span>
                  <p className="text-slate-800 dark:text-slate-100">{selectedRequest.accessName}</p>
                </div>
                <div>
                  <span className="font-medium text-slate-600 dark:text-slate-300">Policies:</span>
                  <p className="text-slate-800 dark:text-slate-100">{selectedRequest.selectedPolicies}</p>
                </div>
                <div>
                  <span className="font-medium text-slate-600 dark:text-slate-300">Access Type:</span>
                  <p className="text-slate-800 dark:text-slate-100">{selectedRequest.accessType}</p>
                </div>
                <div>
                  <span className="font-medium text-slate-600 dark:text-slate-300">Justification:</span>
                  <p className="text-slate-800 dark:text-slate-100 mt-1">{selectedRequest.requestMessage}</p>
                </div>
              </div>

              {detailsWorkflow?.groupedApprovals?.some((group) => group.approvals.length > 0) && (
                <MultiApprovalGroupedHistory groupedApprovals={detailsWorkflow.groupedApprovals} />
              )}

              {selectedRequest.requestStatus === "PENDING" && isMultiApprovalRequest(detailsWorkflow) && (
                <MultiApprovalProgressPanel ruleProgress={detailsWorkflow.ruleProgress} />
              )}

              {selectedRequest.requestStatus === "PENDING" && (
                <div className="flex justify-end gap-3 pt-4 border-t">
                  <Button variant="outline" onClick={() => setIsDetailsOpen(false)}>
                    Close
                  </Button>
                  {role === "TENANT_USER" ? (
                    <Button
                      variant="outline"
                      disabled={loadingIds.has(selectedRequest.id)}
                      className="text-purple-600 border-purple-600 hover:bg-purple-50 dark:hover:bg-purple-950"
                      onClick={() => handleRevoke(selectedRequest.id)}
                    >
                      <RotateCcw className="w-4 h-4 mr-1" /> Cancel Request
                    </Button>
                  ) : <>
                    <Button
                      variant="destructive"
                      disabled={loadingIds.has(selectedRequest.id) || !canUserDeclineDetails()}
                      className="disabled:opacity-50"
                      onClick={() => {
                        setIsDetailsOpen(false);
                        handleRequest("DECLINE", selectedRequest.id, selectedRequest)
                      }}
                    >
                      <XCircle className="w-4 h-4 mr-1" />
                      Decline
                    </Button>
                    <Button
                      disabled={loadingIds.has(selectedRequest.id) || !canUserApprove(selectedRequest.id)}
                      onClick={() => {
                        handleRequest("APPROVED", selectedRequest.id, selectedRequest);
                      }}

                      className="bg-green-600 hover:bg-green-700 disabled:opacity-50"
                    >
                      <CheckCircle2 className="w-4 h-4 mr-1" />
                      Approve
                    </Button>
                  </>}

                </div>
              )}

              {selectedRequest.requestStatus === "APPROVED" && (
                <div className="flex justify-end gap-3 pt-4 border-t">
                  <Button variant="outline" onClick={() => setIsDetailsOpen(false)}>
                    Close
                  </Button>
                  <Button
                    variant="outline"
                    disabled={loadingIds.has(selectedRequest.id)}
                    className="text-purple-600 border-purple-600 hover:bg-purple-50 dark:hover:bg-purple-950"
                    onClick={() => handleRevoke(selectedRequest.id)}
                  >
                    <RotateCcw className="w-4 h-4 mr-1" /> Revoke
                  </Button>
                </div>
              )}

            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
